import { addComponent, addEntity, createWorld, query, removeEntity } from "bitecs";
import { DT, HISTORY_TICKS, KO_WINDOW, MAX_REWIND_TICKS, RESPAWN_TICKS, Team } from "../constants.ts";
import { KITS, PROJECTILES, ZONES, heroDef } from "../heroes/roster/index.ts";
import { abilityInfo, heldWeapon, heroCapsules, type HeroDef, HeroId, SLOT_COUNT } from "../heroes/types.ts";
import { inAabb, type MapData, type TrackPose } from "../map/mapdata.ts";
import type { Vec3 } from "../math.ts";
import type { InputCmd } from "../net/input.ts";
import { abilIdx, clearEntity, createStores, HERO_VARS, MoveKind, type Stores } from "./components.ts";
import { constructObstacles, stepConstructs } from "./constructs.ts";
import type { SimEvent } from "./events.ts";
import { releaseHeldBy, releaseHeldTarget, updateHeldPlayers } from "./held.ts";
import { stepMovement } from "./movement.ts";
import { endTargeting, stepTargeting } from "./targeting.ts";
import { addUlt, applyHeal, clearProjectilePierce, passiveUltRate, stepDamageFilters } from "./combat.ts";
import { PhysicsWorld, type Obstacle } from "./physics.ts";

export interface PredictedProjectile {
  kind: number;
  owner: number;
  origin: Vec3;
  vel: Vec3;
  gravity?: number;
  radius?: number;
  seconds: number;
  seq: number;
  age: number;
}

export interface PlayerMeta {
  name: string;
  bot: boolean;
  stats: PlayerStats;
  deathPos?: Vec3;
  /** Enemies who damaged this player (eid → tick of their last hit): they share the KO. */
  damagedBy: Map<number, number>;
  /** Allies enabling this player (eid → tick their help lapses): heals, buffs, overhealth, revives. */
  supportedBy: Map<number, number>;
  /** Enemies who crowd-controlled or debuffed this player (eid → tick it lapses). */
  disabledBy: Map<number, number>;
}

export interface KillCredit {
  /** The Final Hit: the killer, or for environmental deaths the last enemy to hit the victim. */
  final?: number;
  /** Everyone credited with the KO, final hit first. */
  kos: number[];
  assists: number[];
}

export interface PlayerStats {
  /** KOs: kills you took part in (damaged the victim within KO_WINDOW), as Rivals counts them. */
  kills: number;
  /** Killing blows. */
  finalHits: number;
  deaths: number;
  assists: number;
  damage: number;
  healing: number;
  mitigated: number;
  objectiveTicks: number;
}

export const emptyStats = (): PlayerStats => ({ kills: 0, finalHits: 0, deaths: 0, assists: 0, damage: 0, healing: 0, mitigated: 0, objectiveTicks: 0 });

interface HistoryEntry {
  x: number;
  y: number;
  z: number;
  crouch: number;
  alive: number;
}

/**
 * Rivals' health packs: a mini pack heals 150 at once; a mega pack heals 150 at once and 150 more
 * over the next few seconds, which taking damage cancels. Both come back 20 s after pickup.
 */
export const HEALTH_PACK = { instant: 150, largeOverTime: 150, overTimeSeconds: 3, cooldownSeconds: 20 } as const;
/** Regenerative Shield: starts after this long without damage, then refills fully over as long again. */
export const SHIELD_REGEN_DELAY_TICKS = 5 * 60;
export const SHIELD_REGEN_SECONDS = 5;

export interface HealthPackState {
  id: number;
  large: boolean;
  pos: Vec3;
  cooldown: number;
}

export interface SimOptions {
  /** Server = true. Client prediction sims set false: no effects on other entities. */
  authority: boolean;
}

/**
 * The shared deterministic simulation. The server runs one per match; the client runs one
 * containing only its own player for prediction/reconciliation.
 */
export class Sim {
  readonly c: Stores = createStores();
  readonly world = createWorld();
  readonly physics: PhysicsWorld;
  readonly authority: boolean;
  tick = 0;
  /** True while the client re-simulates inputs during reconciliation (suppresses effects). */
  replaying = false;
  events: SimEvent[] = [];
  /** Client-only: projectiles spawned by local prediction, drained by the renderer. */
  predictedProjectiles: PredictedProjectile[] = [];
  private nextEventId = 1;
  readonly players = new Map<number, PlayerMeta>();
  readonly netToEid = new Map<number, number>();
  private nextNetId = 2; // 1 reserved for match state
  readonly healthPacks: HealthPackState[];
  /** Player eid → tick until which portals won't teleport them again. */
  readonly teleportCd = new Map<number, number>();
  /** Lag compensation: tick (fractional) that hitbox queries should use; undefined = present. */
  rewindTick: number | undefined;
  private readonly history: Map<number, HistoryEntry>[] = [];
  /** Hook for the match layer: called when a player dies. */
  onKill?: (victim: number, killer: number | undefined) => void;
  /** Spawn-rule hook (match layer decides whether respawn is allowed). */
  canRespawn: (eid: number) => boolean = () => true;
  /** Set by the match layer while a round is in progress: heroes gain passive ult energy. */
  ultPassive = false;
  /** Mega health pack heal-over-time still owed (player eid → HP). Server only. */
  private readonly packHeal = new Map<number, number>();
  /** Damage/healing multiplier hook (e.g. dev god mode). */
  damageFilter?: (target: number, amount: number) => number;
  /** Which map side's spawns and spawn rooms a team uses (Convoy: the attackers take side A). */
  spawnSide: (team: Team) => Team = (t) => t;
  /** Spawn stage in use per map side [A, B] (Convoy: moves forward with the checkpoints). */
  spawnStage: [number, number] = [0, 0];
  /** Convoy: where the payload is (its obstacles live in physics.obstacles). */
  cart?: TrackPose;
  /** Non-construct analytic obstacles owned by the match layer (payload, gates). */
  baseObstacles: Obstacle[] = [];

  constructor(
    readonly map: MapData,
    opts: SimOptions,
  ) {
    this.physics = new PhysicsWorld(map);
    this.authority = opts.authority;
    this.healthPacks = map.healthPacks.map((h) => ({ ...h, cooldown: 0 }));
  }

  // ------------------------------------------------------------------ entities

  createEntity(netId?: number): number {
    const eid = addEntity(this.world);
    clearEntity(this.c, eid);
    const id = netId ?? this.allocNetId();
    this.c.Net.id[eid] = id;
    this.netToEid.set(id, eid);
    return eid;
  }

  private allocNetId(): number {
    for (let i = 0; i < 65000; i++) {
      const id = this.nextNetId;
      this.nextNetId = this.nextNetId >= 65000 ? 2 : this.nextNetId + 1;
      if (!this.netToEid.has(id)) return id;
    }
    throw new Error("out of net ids");
  }

  destroyEntity(eid: number) {
    this.netToEid.delete(this.c.Net.id[eid]!);
    this.physics.removeCapsule(eid);
    clearProjectilePierce(this, eid);
    // bitECS hands this eid to the next entity created, so no one may keep KO credit against it.
    if (this.players.delete(eid))
      for (const m of this.players.values()) {
        m.damagedBy.delete(eid);
        m.supportedBy.delete(eid);
        m.disabledBy.delete(eid);
      }
    removeEntity(this.world, eid);
  }

  eidOf(netId: number): number | undefined {
    return this.netToEid.get(netId);
  }

  playerEids(): readonly number[] {
    return query(this.world, [this.c.IsPlayer]) as unknown as number[];
  }
  projectileEids(): readonly number[] {
    return query(this.world, [this.c.IsProjectile]) as unknown as number[];
  }
  zoneEids(): readonly number[] {
    return query(this.world, [this.c.IsZone]) as unknown as number[];
  }
  constructEids(): readonly number[] {
    return query(this.world, [this.c.IsConstruct]) as unknown as number[];
  }

  /** Rebuilds analytic obstacles from match obstacles plus solid constructs. */
  refreshObstacles() {
    this.physics.obstacles = [...this.baseObstacles, ...constructObstacles(this)];
  }

  hero(eid: number): HeroDef {
    return heroDef(this.c.Player.hero[eid] as HeroId, this.c.Player.form[eid]!);
  }

  emit(e: SimEvent) {
    if (this.replaying) return;
    e.id = this.nextEventId = (this.nextEventId + 1) & 0xffff || 1;
    e.tick = this.tick;
    this.events.push(e);
  }

  // ------------------------------------------------------------------ players

  addPlayer(team: Team, hero: HeroId, name: string, bot = false, netId?: number): number {
    const eid = this.createEntity(netId);
    addComponent(this.world, eid, this.c.IsPlayer);
    this.c.Player.team[eid] = team;
    this.players.set(eid, { name, bot, stats: emptyStats(), damagedBy: new Map(), supportedBy: new Map(), disabledBy: new Map() });
    this.setHero(eid, hero);
    this.respawn(eid);
    return eid;
  }

  setHero(eid: number, hero: HeroId) {
    const c = this.c;
    if (this.isDisguised(eid)) this.endDisguise(eid);
    const def = heroDef(hero, 0);
    const keepUlt = c.Player.hero[eid] === hero ? c.Ult.charge[eid]! : 0;
    releaseHeldBy(this, eid);
    releaseHeldTarget(this, eid);
    c.Player.hero[eid] = hero;
    c.Player.baseHero[eid] = hero;
    c.Player.form[eid] = 0;
    c.Player.disguiseTicks[eid] = 0;
    this.physics.addCapsule(eid, def.radius, def.height);
    this.resetHeroState(eid);
    c.Ult.charge[eid] = keepUlt;
  }

  private applyFormPools(eid: number, def: HeroDef, hp: "frac" | "full" | "keep", setAmmo: boolean) {
    const c = this.c;
    const totalMax = Math.max(1, c.Health.maxHp[eid]! + c.Health.maxArmor[eid]! + c.Health.maxShield[eid]!);
    const totalNow = c.Health.hp[eid]! + c.Health.armor[eid]! + c.Health.shield[eid]!;
    const frac = Math.max(0, Math.min(1, totalNow / totalMax));
    c.Health.maxHp[eid] = def.health;
    c.Health.maxArmor[eid] = def.armor;
    c.Health.maxShield[eid] = def.shields;
    if (hp !== "keep") {
      const k = hp === "full" ? 1 : frac;
      c.Health.hp[eid] = def.health * k;
      c.Health.armor[eid] = def.armor * k;
      c.Health.shield[eid] = def.shields * k;
    } else {
      c.Health.hp[eid] = Math.min(c.Health.hp[eid]!, def.health);
      c.Health.armor[eid] = Math.min(c.Health.armor[eid]!, def.armor);
      c.Health.shield[eid] = Math.min(c.Health.shield[eid]!, def.shields);
    }
    if (setAmmo) this.resetWeapons(eid, def);
  }

  /** Switches to a form of the current hero, keeping ability cooldowns/charges and ult charge. */
  setForm(eid: number, form: number, o: { hp?: "frac" | "full" | "keep" } = {}) {
    const c = this.c;
    const next = Math.max(0, Math.min(3, Math.floor(form)));
    if (c.Player.form[eid] === next) return;
    c.Player.form[eid] = next;
    const def = this.hero(eid);
    c.Player.crouch[eid] = 0;
    this.physics.resizeCapsule(eid, def.radius, def.height);
    this.applyFormPools(eid, def, o.hp ?? "frac", true);
    this.physics.move(eid, this.posOf(eid), { x: 0, y: 0, z: 0 });
    this.emit({ type: "form", target: c.Net.id[eid]!, hero: c.Player.hero[eid]!, form: next });
  }

  isDisguised(eid: number) {
    const c = this.c;
    return c.Player.disguiseTicks[eid]! > 0 && c.Player.baseHero[eid] !== c.Player.hero[eid];
  }

  /** Reverts a temporary disguise to the stored base hero in form 0. */
  endDisguise(eid: number) {
    const c = this.c;
    if (c.Player.baseHero[eid] === c.Player.hero[eid]) {
      c.Player.disguiseTicks[eid] = 0;
      return;
    }
    const keepUlt = c.Ult.charge[eid]!;
    const base = c.Player.baseHero[eid] as HeroId;
    c.Player.hero[eid] = base;
    c.Player.form[eid] = 0;
    c.Player.disguiseTicks[eid] = 0;
    c.Player.crouch[eid] = 0;
    const def = this.hero(eid);
    this.physics.resizeCapsule(eid, def.radius, def.height);
    this.applyFormPools(eid, def, "frac", true);
    KITS[def.id].init(this, eid);
    c.Ult.charge[eid] = keepUlt;
    this.emit({ type: "form", target: c.Net.id[eid]!, hero: base, form: 0 });
  }

  /** Temporarily makes this player use another hero's def, body and kit until the timer expires. */
  disguise(eid: number, asHero: HeroId, seconds: number, o: { keepUlt?: boolean; hpFrac?: boolean } = {}) {
    const c = this.c;
    const ult = c.Ult.charge[eid]!;
    if (!this.isDisguised(eid)) c.Player.baseHero[eid] = c.Player.hero[eid]!;
    c.Player.hero[eid] = asHero;
    c.Player.form[eid] = 0;
    c.Player.disguiseTicks[eid] = Math.max(1, Math.round(seconds * 60));
    c.Player.crouch[eid] = 0;
    const def = this.hero(eid);
    this.physics.resizeCapsule(eid, def.radius, def.height);
    this.applyFormPools(eid, def, o.hpFrac === false ? "keep" : "frac", true);
    for (let s = 0; s < SLOT_COUNT; s++) {
      c.Abil.cd[abilIdx(eid, s)] = 0;
      c.Abil.charges[abilIdx(eid, s)] = abilityInfo(def, s)?.charges ?? 1;
    }
    c.HeroVars.fill(0, eid * HERO_VARS, eid * HERO_VARS + HERO_VARS);
    KITS[def.id].init(this, eid);
    if (o.keepUlt === false) c.Ult.charge[eid] = 0;
    else c.Ult.charge[eid] = ult;
    this.emit({ type: "form", target: c.Net.id[eid]!, hero: asHero, form: 0 });
  }

  resetHeroState(eid: number) {
    const c = this.c;
    const def = this.hero(eid);
    c.Health.maxHp[eid] = def.health;
    c.Health.maxArmor[eid] = def.armor;
    c.Health.maxShield[eid] = def.shields;
    c.Health.hp[eid] = def.health;
    c.Health.armor[eid] = def.armor;
    c.Health.shield[eid] = def.shields;
    c.Health.over[eid] = 0;
    c.Health.overTicks[eid] = 0;
    this.resetWeapons(eid, def);
    endTargeting(this, eid);
    c.Player.aimMask[eid] = 0;
    c.Weapon.fireCd[eid] = 0;
    c.Ult.active[eid] = 0;
    c.Move.kind[eid] = MoveKind.None;
    c.Move.ticks[eid] = 0;
    for (const k of ["stun", "root", "slow", "sleep", "invuln", "knockdown", "boost", "phased", "antiHeal", "healBoost", "resist", "linked", "stealth", "haste", "unstoppable", "marked", "markedBy", "bonded", "bondedBy", "frozen", "held", "heldBy"] as const)
      c.Status[k][eid] = 0;
    c.Status.slowPct[eid] = 0;
    c.Status.hastePct[eid] = 0;
    for (let s = 0; s < SLOT_COUNT; s++) {
      c.Abil.cd[abilIdx(eid, s)] = 0;
      c.Abil.charges[abilIdx(eid, s)] = abilityInfo(def, s)?.charges ?? 1;
    }
    c.HeroVars.fill(0, eid * HERO_VARS, eid * HERO_VARS + HERO_VARS);
    KITS[def.id].init(this, eid);
  }

  /** First weapon in hand, both magazines full. */
  private resetWeapons(eid: number, def: HeroDef) {
    const c = this.c;
    c.Weapon.slot[eid] = 0;
    c.Weapon.ammo[eid] = def.ammo;
    c.Weapon.stowed[eid] = def.weapons?.[1].ammo ?? 0;
    c.Weapon.reload[eid] = 0;
  }

  /** The spawn points `team` uses right now. */
  activeSpawns(team: Team) {
    const side = this.spawnSide(team);
    const stage = this.spawnStage[side === Team.B ? 1 : 0];
    const all = this.map.spawns.filter((s) => s.team === side);
    const staged = all.filter((s) => s.stage === stage);
    return staged.length ? staged : all;
  }

  respawn(eid: number) {
    const c = this.c;
    const team = c.Player.team[eid] as Team;
    const spawns = this.activeSpawns(team);
    const taken = this.playerEids().filter((o) => o !== eid && c.Player.alive[o]);
    let best = spawns[0]!;
    let bestScore = -Infinity;
    for (const s of spawns) {
      let minD = 99;
      for (const o of taken) minD = Math.min(minD, Math.hypot(c.Pos.x[o]! - s.pos.x, c.Pos.z[o]! - s.pos.z));
      if (minD > bestScore) {
        bestScore = minD;
        best = s;
      }
    }
    c.Pos.x[eid] = best.pos.x;
    c.Pos.y[eid] = best.pos.y;
    c.Pos.z[eid] = best.pos.z;
    c.Vel.x[eid] = c.Vel.y[eid] = c.Vel.z[eid] = 0;
    c.Ext.x[eid] = c.Ext.z[eid] = 0;
    c.Look.yaw[eid] = best.yaw;
    c.Look.pitch[eid] = 0;
    c.Player.alive[eid] = 1;
    c.Player.respawn[eid] = 0;
    c.Player.crouch[eid] = 0;
    if (this.isDisguised(eid)) this.endDisguise(eid);
    c.Player.form[eid] = 0;
    c.Player.baseHero[eid] = c.Player.hero[eid]!;
    c.Player.disguiseTicks[eid] = 0;
    this.physics.setCapsuleHeight(eid, best.pos, this.hero(eid).height);
    this.resetHeroState(eid);
    // A new life, including the fresh start of a round, owes nothing to hits or help from before.
    this.clearCredit(eid);
  }

  /** Drops every KO and assist claim on this player's current life. */
  private clearCredit(eid: number) {
    const m = this.players.get(eid);
    if (!m) return;
    m.damagedBy.clear();
    m.supportedBy.clear();
    m.disabledBy.clear();
  }

  removePlayer(eid: number) {
    this.packHeal.delete(eid);
    this.destroyEntity(eid);
  }

  inSpawnRoom(eid: number): boolean {
    const side = this.spawnSide(this.c.Player.team[eid] as Team);
    const stage = this.spawnStage[side === Team.B ? 1 : 0];
    const p = this.posOf(eid);
    return this.map.spawnRooms.some((r) => r.team === side && r.stage === stage && inAabb(p, r));
  }

  posOf(eid: number): Vec3 {
    return { x: this.c.Pos.x[eid]!, y: this.c.Pos.y[eid]!, z: this.c.Pos.z[eid]! };
  }

  kill(victim: number, killer: number | undefined) {
    const c = this.c;
    if (!c.Player.alive[victim]) return;
    releaseHeldBy(this, victim);
    releaseHeldTarget(this, victim);
    if (this.isDisguised(victim)) this.endDisguise(victim);
    c.Player.form[victim] = 0;
    c.Player.baseHero[victim] = c.Player.hero[victim]!;
    c.Player.disguiseTicks[victim] = 0;
    c.Player.alive[victim] = 0;
    c.Player.respawn[victim] = RESPAWN_TICKS;
    this.packHeal.delete(victim);
    c.Health.hp[victim] = 0;
    c.Move.ticks[victim] = 0;
    c.Ult.active[victim] = 0;
    const meta = this.players.get(victim);
    if (meta) {
      meta.stats.deaths++;
      meta.deathPos = this.posOf(victim);
    }
    const credit = this.creditKill(victim, killer);
    for (const k of credit.kos) this.players.get(k)!.stats.kills++;
    if (credit.final !== undefined) this.players.get(credit.final)!.stats.finalHits++;
    for (const a of credit.assists) this.players.get(a)!.stats.assists++;
    this.clearCredit(victim);
    const f = credit.final;
    this.emit({
      type: "kill",
      killer: f !== undefined ? c.Net.id[f]! : 0,
      victim: c.Net.id[victim]!,
      killerHero: f !== undefined ? c.Player.baseHero[f]! : 255,
      victimHero: c.Player.baseHero[victim]!,
      kos: credit.kos.filter((k) => k !== f).map((k) => c.Net.id[k]!),
      assists: credit.assists.map((a) => c.Net.id[a]!),
    });
    KITS[c.Player.hero[victim] as HeroId].onDeath?.(this, victim, f);
    this.onKill?.(victim, f);
  }

  /** Who gets the KO, the Final Hit and the assists for `victim` dying now (see KO_WINDOW). */
  creditKill(victim: number, killer: number | undefined): KillCredit {
    const c = this.c;
    const meta = this.players.get(victim);
    if (!meta) return { kos: [], assists: [] };
    const now = this.tick;
    const team = c.Player.team[victim];
    const enemy = (e: number) => e !== victim && this.players.has(e) && c.Player.team[e] !== team;
    // Most recent attacker first: for an environmental death the Final Hit falls to them.
    const kos = [...meta.damagedBy]
      .filter(([a, t]) => enemy(a) && now - t <= KO_WINDOW)
      .sort((x, y) => y[1] - x[1])
      .map(([a]) => a);
    const final = killer !== undefined && enemy(killer) ? killer : kos[0];
    if (final !== undefined) {
      const i = kos.indexOf(final);
      if (i >= 0) kos.splice(i, 1);
      kos.unshift(final);
    }
    const assists = new Set<number>();
    const consider = (e: number, until: number) => {
      if (until >= now && enemy(e) && !kos.includes(e)) assists.add(e);
    };
    for (const k of kos) for (const [s, until] of this.players.get(k)!.supportedBy) consider(s, until);
    for (const [s, until] of meta.disabledBy) consider(s, until);
    return { final, kos, assists: [...assists] };
  }

  // ------------------------------------------------------------------ per-player step (predicted)

  /**
   * Advances one player by one tick using `input`. Runs identically on the server and in the
   * client's prediction sim. Everything here must only depend on this player's state + map.
   */
  stepPlayer(eid: number, input: InputCmd) {
    const c = this.c;
    this.rewindTick = this.authority ? input.renderTick : undefined;
    try {
      this.stepPlayerInner(eid, input);
    } finally {
      this.rewindTick = undefined;
      c.Player.buttons[eid] = input.buttons;
    }
  }

  private stepPlayerInner(eid: number, input: InputCmd) {
    const c = this.c;
    const prevButtons = c.Player.buttons[eid]!;
    if (c.Player.disguiseTicks[eid]! > 0) {
      c.Player.disguiseTicks[eid] = c.Player.disguiseTicks[eid]! - 1;
      if (c.Player.disguiseTicks[eid]! <= 0) this.endDisguise(eid);
    }
    const def = this.hero(eid);
    // Timers
    for (let s = 0; s < SLOT_COUNT; s++) {
      const i = abilIdx(eid, s);
      if (c.Abil.cd[i]! > 0) {
        c.Abil.cd[i] = c.Abil.cd[i]! - 1;
        const info = abilityInfo(def, s);
        const maxCharges = info?.charges ?? 1;
        if (c.Abil.cd[i]! <= 0 && c.Abil.charges[i]! < maxCharges) {
          c.Abil.charges[i] = c.Abil.charges[i]! + 1;
          if (c.Abil.charges[i]! < maxCharges) c.Abil.cd[i] = Math.round((info?.cooldown ?? 0) * 60);
          else c.Abil.cd[i] = 0;
        }
      }
    }
    const S = c.Status;
    for (const k of ["stun", "root", "slow", "sleep", "invuln", "knockdown", "boost", "phased", "antiHeal", "healBoost", "resist", "linked", "stealth", "haste", "unstoppable", "marked", "bonded", "frozen", "held"] as const) if (S[k][eid]! > 0) S[k][eid] = S[k][eid]! - 1;
    if (S.held[eid]! <= 0 && S.heldBy[eid]! > 0) releaseHeldTarget(this, eid);
    if (c.Weapon.fireCd[eid]! > 0) c.Weapon.fireCd[eid] = c.Weapon.fireCd[eid]! - 1;
    if (c.Weapon.actionTicks[eid]! > 0) c.Weapon.actionTicks[eid] = c.Weapon.actionTicks[eid]! - 1;
    else c.Weapon.action[eid] = 0;
    if (c.Weapon.reload[eid]! > 0) {
      c.Weapon.reload[eid] = c.Weapon.reload[eid]! - 1;
      if (c.Weapon.reload[eid] === 0) c.Weapon.ammo[eid] = heldWeapon(def, c.Weapon.slot[eid]!).ammo;
    }
    if (c.Ult.active[eid]! > 0) c.Ult.active[eid] = c.Ult.active[eid]! - 1;
    if (c.Health.overTicks[eid]! > 0) {
      c.Health.overTicks[eid] = c.Health.overTicks[eid]! - 1;
      if (c.Health.overTicks[eid] === 0) c.Health.over[eid] = 0;
    }

    c.Look.yaw[eid] = input.yaw;
    c.Look.pitch[eid] = input.pitch;

    if (!c.Player.alive[eid]) return;

    const disabled = S.stun[eid]! > 0 || S.sleep[eid]! > 0 || S.knockdown[eid]! > 0 || S.frozen[eid]! > 0 || S.held[eid]! > 0;
    if (disabled && c.Move.kind[eid] !== MoveKind.Pulled) {
      c.Move.ticks[eid] = 0;
    }
    const pressed = input.buttons & ~prevButtons;
    const aimed = stepTargeting(this, eid, input, pressed, !disabled);
    KITS[def.id].tick(this, eid, aimed.input, aimed.pressed, !disabled);
    if (S.held[eid]! > 0) return;
    stepMovement(this, eid, input, pressed, disabled);
  }

  // ------------------------------------------------------------------ world step (authoritative)

  /** Server tick: steps every player with its input, then projectiles/zones/regen/respawns. */
  stepWorld() {
    const c = this.c;
    // Projectiles
    // Copy: projectiles may be destroyed while iterating the live query.
    // oxlint-disable-next-line unicorn/no-useless-spread
    for (const p of [...this.projectileEids()]) {
      const kind = PROJECTILES[c.Projectile.kind[p]!];
      if (!kind) {
        this.destroyEntity(p);
        continue;
      }
      kind.tick(this, p);
    }
    // Zones
    // oxlint-disable-next-line unicorn/no-useless-spread
    for (const z of [...this.zoneEids()]) {
      c.Zone.age[z] = c.Zone.age[z]! + 1;
      ZONES[c.Zone.kind[z]!]?.tick(this, z);
      c.Zone.ticks[z] = c.Zone.ticks[z]! - 1;
      if (c.Zone.ticks[z]! <= 0) {
        ZONES[c.Zone.kind[z]!]?.end?.(this, z);
        this.destroyEntity(z);
      }
    }
    // Constructs
    stepConstructs(this);
    updateHeldPlayers(this);
    stepDamageFilters(this);
    // Players: regen, respawn, kill plane, health packs
    for (const eid of this.playerEids()) {
      if (!c.Player.alive[eid]) {
        if (c.Player.respawn[eid]! > 0) c.Player.respawn[eid] = c.Player.respawn[eid]! - 1;
        if (c.Player.respawn[eid]! <= 0 && this.canRespawn(eid)) this.respawn(eid);
        continue;
      }
      c.Health.sinceDamage[eid] = c.Health.sinceDamage[eid]! + 1;
      const since = c.Health.sinceDamage[eid]!;
      const maxShield = c.Health.maxShield[eid]!;
      if (maxShield > 0 && since > SHIELD_REGEN_DELAY_TICKS && c.Health.shield[eid]! < maxShield)
        c.Health.shield[eid] = Math.min(maxShield, c.Health.shield[eid]! + (maxShield / SHIELD_REGEN_SECONDS) * DT);
      const owed = this.packHeal.get(eid);
      if (owed !== undefined) {
        // sinceDamage was zeroed by a hit during the last tick.
        const step = Math.min(owed, (HEALTH_PACK.largeOverTime / HEALTH_PACK.overTimeSeconds) * DT);
        if (since <= 1 || owed <= step) this.packHeal.delete(eid);
        else this.packHeal.set(eid, owed - step);
        if (since > 1) applyHeal(this, eid, eid, step, { silent: true, noUlt: true });
      }
      if (this.ultPassive) addUlt(this, eid, passiveUltRate(this, eid) * DT, "energy");
      // A fall is environmental: whoever knocked them off inside the KO window gets the Final Hit.
      if (c.Pos.y[eid]! < this.map.bounds.min.y - 15) this.kill(eid, undefined);
      if (this.inSpawnRoom(eid) && since > 30) applyHeal(this, eid, eid, 250 * DT, { silent: true, noUlt: true });
    }
    for (const hp of this.healthPacks) {
      if (hp.cooldown > 0) {
        hp.cooldown--;
        continue;
      }
      for (const eid of this.playerEids()) {
        if (!c.Player.alive[eid]) continue;
        const missing = c.Health.maxHp[eid]! + c.Health.maxArmor[eid]! + c.Health.maxShield[eid]! - (c.Health.hp[eid]! + c.Health.armor[eid]! + c.Health.shield[eid]!);
        if (missing < 1) continue;
        const dx = c.Pos.x[eid]! - hp.pos.x;
        const dy = c.Pos.y[eid]! - hp.pos.y;
        const dz = c.Pos.z[eid]! - hp.pos.z;
        if (dx * dx + dz * dz < 1.2 * 1.2 && Math.abs(dy) < 1.5) {
          applyHeal(this, eid, eid, HEALTH_PACK.instant, { silent: false, noUlt: true });
          if (hp.large) this.packHeal.set(eid, HEALTH_PACK.largeOverTime);
          hp.cooldown = HEALTH_PACK.cooldownSeconds * 60;
          this.emit({ type: "pickup", pos: hp.pos, large: hp.large });
          break;
        }
      }
    }
    this.tick++;
    this.recordHistory();
  }

  // ------------------------------------------------------------------ lag compensation

  private recordHistory() {
    const c = this.c;
    const m = new Map<number, HistoryEntry>();
    for (const eid of this.playerEids())
      m.set(eid, { x: c.Pos.x[eid]!, y: c.Pos.y[eid]!, z: c.Pos.z[eid]!, crouch: c.Player.crouch[eid]!, alive: c.Player.alive[eid]! });
    this.history[this.tick % HISTORY_TICKS] = m;
  }

  /** Position of `eid` at the current rewind time (or present). */
  targetPos(eid: number): { pos: Vec3; crouch: boolean; alive: boolean } {
    const c = this.c;
    const present = { pos: this.posOf(eid), crouch: !!c.Player.crouch[eid], alive: !!c.Player.alive[eid] };
    if (this.rewindTick === undefined) return present;
    const t = Math.max(this.tick - MAX_REWIND_TICKS, Math.min(this.tick, this.rewindTick));
    const t0 = Math.floor(t);
    const f = t - t0;
    const a = this.history[t0 % HISTORY_TICKS]?.get(eid);
    const b = this.history[(t0 + 1) % HISTORY_TICKS]?.get(eid);
    if (!a || t0 < 0) return present;
    if (!b || f === 0 || t0 + 1 > this.tick) return { pos: { x: a.x, y: a.y, z: a.z }, crouch: !!a.crouch, alive: !!a.alive };
    return {
      pos: { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, z: a.z + (b.z - a.z) * f },
      crouch: !!a.crouch,
      alive: !!a.alive && !!b.alive,
    };
  }

  hitCapsules(eid: number) {
    const tp = this.targetPos(eid);
    const def = this.hero(eid);
    return {
      alive: tp.alive,
      pos: tp.pos,
      capsules: heroCapsules(def, tp.crouch).map((cap) => ({
        a: { x: tp.pos.x, y: tp.pos.y + cap.a, z: tp.pos.z },
        b: { x: tp.pos.x, y: tp.pos.y + cap.b, z: tp.pos.z },
        r: cap.r,
        head: cap.head,
      })),
    };
  }
}

export const setForm = (sim: Sim, eid: number, form: number, o?: { hp?: "frac" | "full" | "keep" }) => sim.setForm(eid, form, o);
export const disguise = (sim: Sim, eid: number, asHero: HeroId, seconds: number, o?: { keepUlt?: boolean; hpFrac?: boolean }) => sim.disguise(eid, asHero, seconds, o);
export const isDisguised = (sim: Sim, eid: number) => sim.isDisguised(eid);
export const endDisguise = (sim: Sim, eid: number) => sim.endDisguise(eid);
